import { UserType } from "global";
import { useForm } from "../../../hooks/useForm";
import Modal, { ModalProps } from "../../common/components/Modal";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import SelectBox from "../../common/components/SelectBox";
import SearchBar from "../../common/components/SearchBar";
import Table from "../../common/components/Table";
import {
  useSearchCaregiversAsAdminLazyQuery,
  useSearchGuardiansAsAdminLazyQuery,
} from "../../../lib/apollo/graphql/generated";
import { FETCH_SIZE } from "../../../utilities/constants";
import { formatDate } from "../../../utilities/format";
import FetchMore from "../../common/components/FetchMore";
import { useInView } from "react-intersection-observer";
import { SelectUserParams } from "../containers/IncentiveDashboardContainer";

interface SearchUserModalProps extends ModalProps {
  onSelect: (params: SelectUserParams) => void;
}

type SearchUserFilters = {
  query: string;
  type: UserType;
};

export default function SearchUserModal({
  open,
  onClose,
  onSelect,
}: SearchUserModalProps) {
  const [filters, onChange] = useForm<SearchUserFilters>({
    query: "",
    type: "caregivers",
  });

  const isCaregiverSearch = filters.type === "caregivers";

  const [
    searchGuardians,
    {
      data: gData,
      loading: isSearchingGuardians,
      fetchMore: fetchMoreGuardians,
      networkStatus: gNetworkStatus,
    },
  ] = useSearchGuardiansAsAdminLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const [
    searchCaregivers,
    {
      data: cData,
      loading: isSearchingCaregivers,
      fetchMore: fetchMoreCaregivers,
      networkStatus: cNetworkStatus,
    },
  ] = useSearchCaregiversAsAdminLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const onSearch = () => {
    if (!filters.query || !filters.type) {
      return;
    }

    if (isCaregiverSearch) {
      searchCaregivers({
        variables: {
          query: filters.query,
          first: FETCH_SIZE,
        },
      });
    } else {
      searchGuardians({
        variables: {
          query: filters.query,
          first: FETCH_SIZE,
        },
      });
    }
  };

  const users = isCaregiverSearch
    ? cData?.searchCaregiversAsAdmin.edges || []
    : gData?.searchGuardiansAsAdmin.edges || [];

  const pageInfo = isCaregiverSearch
    ? cData?.searchCaregiversAsAdmin.pageInfo
    : gData?.searchGuardiansAsAdmin.pageInfo;

  const fetchMore = isCaregiverSearch
    ? fetchMoreCaregivers
    : fetchMoreGuardians;

  const networkStatus = isCaregiverSearch ? cNetworkStatus : gNetworkStatus;

  const onViewChange = (inView?: boolean) => {
    if (inView && networkStatus !== 4 && pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          query: filters.query,
          first: FETCH_SIZE,
          after: pageInfo?.endCursor,
        },
      });
    }
  };

  const [fetchMoreRef] = useInView({
    threshold: 0.3,
    onChange: onViewChange,
  });

  return (
    <Modal
      title="사용자 검색"
      height="800px"
      width="800px"
      open={open}
      onClose={onClose}
    >
      <div className="flex h-full flex-col gap-3">
        <ListPageTopSection>
          <SelectBox
            value={filters.type}
            onSelect={(userType) => onChange("type", userType)}
            options={[
              { label: "보호사", value: "caregivers" },
              { label: "보호자", value: "guardians" },
            ]}
          />
          <SearchBar
            value={filters.query}
            placeholder={`${isCaregiverSearch ? "보호사" : "보호자"} 이름`}
            onChange={(value) => onChange("query", value.trim())}
            onSearch={onSearch}
          />
        </ListPageTopSection>
        <Table.Wrapper>
          <Table.Header
            tabs={[
              { label: "이름" },
              { label: "닉네임" },
              { label: "가입일자" },
            ]}
          />
          <Table.Body
            isLoading={isSearchingCaregivers || isSearchingGuardians}
            isEmpty={!users?.length}
          >
            {users?.map((user) => (
              <Table.Row
                onClick={() => {
                  if (user.id) {
                    onSelect({
                      type: isCaregiverSearch ? "caregivers" : "guardians",
                      id: user.id,
                      name: user.name,
                    });
                  }
                }}
              >
                <Table.Column>{user.name}</Table.Column>
                <Table.Column>{user.username}</Table.Column>
                <Table.Column>
                  {formatDate(user.createdAt, "YY년 MM월 DD일")}
                </Table.Column>
              </Table.Row>
            ))}
            {pageInfo?.hasNextPage && (
              <FetchMore
                isFetchingMore={networkStatus === 4}
                fetchMoreRef={fetchMoreRef}
              />
            )}
          </Table.Body>
        </Table.Wrapper>
      </div>
    </Modal>
  );
}
